import { HoverActionButton, HoverActionButtonHierarchy, HoverActionButtonSize } from '@vizit/atoms';
import { Image, Tooltip, TooltipTrigger } from '@vizit/atoms';
import { ArrowLeftIcon, ArrowNarrowUpRightIcon } from '@vizit/icons';
import { ImageLegend, type ImageLegendVariant } from '@vizit/molecules';
import { cx } from '@vizit/theme';

interface ArtifactViewerProps {
  /**
   * The name of the asset that will be in the alt text of the image.
   */
  assetName?: string;
  /**
   * The source of the image artifact.
   */
  src: string | null;
  /**
   * Whether the image is loading.
   */
  isLoading: boolean;
  /**
   * Whether the image is broken.
   */
  isBroken: boolean;
  /**
   * The image legend variant.
   */
  imageLegendVariant: ImageLegendVariant;
  /**
   * Custom label for the image legend (overrides default label for the variant).
   */
  legendLabel?: string;
  /**
   * Called when the back overlay action on the preview is pressed. The button
   * only renders when this is provided.
   *
   * @optional
   * @type {() => void}
   * @default undefined (Back button hidden)
   */
  onBack?: () => void;
  /**
   * Called when the jump-to overlay action on the preview is pressed. The
   * button only renders when this is provided.
   *
   * @optional
   * @type {() => void}
   * @default undefined (Jump to button hidden)
   */
  onJumpTo?: () => void;
}

/**
 * Persistent overlay actions on the preview. Colors and hover/focus states
 * come from the secondary `HoverActionButton` hierarchy, the same dark
 * overlay button as on asset tiles; only placement and a large shadow are
 * set here. drop-shadow (a filter) instead of shadow-lg: the button's focus
 * ring is a box-shadow, and a second box-shadow class would replace it.
 */
const overlayButtonClasses = 'absolute drop-shadow-lg';

// A single wide, low-opacity layer so the preview lifts off the dot grid
// without a visible edge. drop-shadow (a filter) rather than shadow-* so it
// follows the image's own alpha on transparent PNGs.
const previewShadowClass = 'drop-shadow-[0_24px_48px_rgba(0,0,0,0.15)]';

const ArtifactViewer = (props: ArtifactViewerProps) => {
  const { assetName = 'Asset', src, isLoading = true, isBroken = false, imageLegendVariant, legendLabel } = props;
  const { onBack, onJumpTo } = props;

  return (
    <div className="h-full w-full max-h-[calc(100vh-153px)] bg-dot-grid py-8 flex flex-col items-center justify-start gap-10 max-lg:border-b max-lg:border-secondary">
      <div className="grid grid-cols-6 justify-center w-full max-w-screen-xl max-4xl:max-w-screen-lg">
        <div className="relative col-start-2 col-span-4 flex items-center justify-center">
          {onBack && (
            <TooltipTrigger>
              <HoverActionButton
                aria-label="Back to source image"
                size={HoverActionButtonSize.Md}
                hierarchy={HoverActionButtonHierarchy.Secondary}
                icon={<ArrowLeftIcon size={24} />}
                onPress={onBack}
                className={cx(overlayButtonClasses, 'left-0')}
              />
              <Tooltip placement="bottom">Back to source image</Tooltip>
            </TooltipTrigger>
          )}
          <ImageLegend variant={imageLegendVariant} leftLabel={legendLabel} />
          {onJumpTo && (
            <TooltipTrigger>
              <HoverActionButton
                aria-label="Jump to image"
                size={HoverActionButtonSize.Md}
                hierarchy={HoverActionButtonHierarchy.Secondary}
                icon={<ArrowNarrowUpRightIcon size={24} />}
                onPress={onJumpTo}
                className={cx(overlayButtonClasses, 'right-0')}
              />
              <Tooltip placement="bottom">Jump to image</Tooltip>
            </TooltipTrigger>
          )}
        </div>
      </div>
      <div className="grid grid-cols-6 justify-center items-start w-full h-full max-w-screen-xl max-4xl:max-w-screen-lg">
        <div className="col-start-2 col-span-4 w-full h-auto object-contain">
          <Image
            isBroken={isBroken}
            src={src}
            isAnalyzing={isLoading}
            name={assetName ?? 'Image Asset'}
            imageClassName={previewShadowClass}
          />
        </div>
      </div>
    </div>
  );
};

export { ArtifactViewer };
export type { ArtifactViewerProps };
